import { NavigationGuardNext, Route } from 'vue-router';
import { useStore } from '../store';
import { ConfigurationPluginManager } from '../plugins/configuration';
import i18next from 'i18next';
import { OrderStepPageDataExtensions } from '@a/store/order/types';
import { GameserverPageData } from '@a/store/game/types';

/**
 * router middleware in use by the beforeEach hook
 *
 * @param route
 */
export const useRouterMiddleware = (
  route: Route,
) => {
  const store = useStore();
  const configurationApi = ConfigurationPluginManager.getInstance();

  const META_REQUIRES_ADMIN = 'requiresAdmin';
  const META_REQUIRES_AUTH = 'requiresAuth';

  const providesTitle = (): boolean => route.meta && 'title' in route.meta;

  const setDocumentTitle = (): void => document.title = route.meta.title;

  const isAuthRequired = (): boolean => route.meta
    && META_REQUIRES_AUTH in route.meta
    && route.meta.requiresAuth === true;

  const isAuthenticated = (): boolean => store.getters['auth/isLoggedIn'];

  /**
   * commits the loaded customer into vuex
   * @param plugin
   */
  const commitAuthWithConfigPlugin = (plugin: ConfigurationPluginManager): void => {
    if ('customer' in plugin.data && plugin.data.customer !== null) {
      store.commit('auth/customerLoaded', plugin.data.customer);
    } else {
      store.commit('auth/customerError');
    }
  };

  /**
   * small middleware to request a namespace
   */
  const requestExtensionNamespace = async (next: NavigationGuardNext): Promise<void> => {
    if (configurationApi == null) {
      return next();
    }

    const { pageData } = configurationApi.data;

    if (pageData != null
      && (
        (pageData as GameserverPageData).useModsSpaceAddon
        || (pageData as OrderStepPageDataExtensions).extensions.length > 0
      )
    ) {
      await i18next.loadNamespaces('extension');
    }

    return next();
  };

  /**
   * returns true if the route should only be accessible by admins
   */
  const isAdminRoute = (): boolean => route.matched
    .some(
      (record) => META_REQUIRES_ADMIN in record.meta && record.meta.requiresAdmin === true,
    );

  return {
    requestExtensionNamespace,
    commitAuthWithConfigPlugin,
    isAuthRequired,
    isAuthenticated,
    isAdminRoute,
    providesTitle,
    setDocumentTitle,
  };
};

/**
 * While the router is loading a route chunk, use this function to reserve the
 * space above the footer to avoid CLS with a spacer, which impacts the SEO Score positively.
 * If something went wrong while loading the route, an error will be shown.
 *
 * Use this function on SEO related pages only!
 * Else let vue-router manage the lazy loading itself.
 *
 * @param asyncRouterView a Promise with async component
 *
 * @returns a Promise.resolve with an async functional component
 */
export const lazyLoadView = (asyncRouterView) => {
  const asyncRouterHandler = () => ({
    component: asyncRouterView,
    // A component to use while the component is loading.
    loading: {
      name: 'AsyncRouterLoadingView',
      template: '<div style="min-height: 100vh;"></div>',
    },
    // A fallback component in case the timeout is exceeded
    // when loading the component.
    error: {
      name: 'AsyncRouterErrorView',
      template: `
        <div class="wrapper" style="min-height: 100vh; padding-top: 100px; text-align:center;">
          <h2>${i18next.t('general:non_special_texts.something_is_wrong')}</h2>
        </div>
      `,
    },
    delay: 0,
    // Time before giving up trying to load the component.
    // Default: Infinity (milliseconds).
    timeout: 10000,
  });

  return Promise.resolve({
    functional: true,
    render(h, { data, children }) {
      // Transparently pass any props or children
      // to the view component.
      return h(asyncRouterHandler, data, children);
    },
  });
};

